import { Typography, Box, Table, TableContainer, TableBody, Button, CircularProgress, IconButton, MenuItem, Tooltip, TablePagination, Paper, useTheme, Menu, Skeleton } from '@mui/material'
import ParticipationAttemptRow from '../../../components/exams-components/ParticipationAttemptRow'
import DataLoadingSpinner from '../../../components/common/DataLoadingSpinner'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { exportExamParticipation } from '../../../utils/exam-utilities'
import BreadcrumbList from '../../../components/common/BreadcrumbList'
import noParticipationImg from '../../../assets/no-participation.svg'
import BlockMessage from '../../../components/common/BlockMessage'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import TableHeader from '../../../components/common/TableHeader'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import { UnfoldMore, Upload } from '@mui/icons-material'
import { Icons, sortArrayOfObjectsByKey } from '../../../utils/utilities'
import { toast } from 'react-toastify'
import api from '../../../service/api'
import CustomSearchBar from '../../../components/common/CustomSearchBar'
import CountCard from '../../../components/common/CountCard'
import { useExamStore } from '../../../store/exam.store'

export default function ExamParticipation () {

    const { id } = useParams()
    const [filteredParticipations, setFilteredParticipations] = useState( [] )
    const [attemptsMenuOpen, setAttemptsMenuOpen] = useState( false )
    const [participations, setParticipations] = useState( [] )
    const [accessDenied, setAccessDenied] = useState( false )
    const [legacyGroup, setLegacyGroup] = useState( false )
    const [inProgress, setInProgress] = useState( true )
    const [rowsPerPage, setRowsPerPage] = useState( 50 )
    const [totalMarks, setTotalMarks] = useState( 0 )
    const [sortState, setSortState] = useState( {} )
    const [exam, setExam] = useState( null )
    const [page, setPage] = useState( 0 )

    const { palette, border, theme, shadows, layout } = useTheme()
    const navigate = useNavigate()

    const ExamStore = useExamStore()

    const attemptsMenuRef = useRef()

    const [searchParams, setSearchParams] = useSearchParams( { selectedAttempt: "0", searchText: "" } )
    const selectedAttempt = searchParams.get( 'selectedAttempt' )
    const searchText = searchParams.get( 'searchText' ) || ""

    const setSearchParamValue = useCallback( ( key, val ) => {
        setSearchParams( prev => {
            prev.set( key, val )
            return prev
        }, { replace: true } )
    }, [setSearchParams] )

    const handleAttemptChange = ( val = selectedAttempt, data = participations, changeValue = true ) => {
        if ( changeValue )
            setSearchParamValue( 'selectedAttempt', val )
        setAttemptsMenuOpen( false )
    }

    const sortByProperty = ( property ) => {
        let newFilteredParticipations = [...filteredParticipations]
        newFilteredParticipations.sort( ( a, b ) => {
            if ( typeof a[property] == "number" ) {
                if ( !sortState[property] )
                    return ( a[property] - b[property] )
                else
                    return ( b[property] - a[property] )
            } else {
                if ( !sortState[property] )
                    return ( ( a[property] < b[property] ) ? -1 : 0 )
                else
                    return ( ( a[property] > b[property] ) ? -1 : 0 )
            }
        } )
        setSortState( { ...sortState, [property]: sortState[property] ? false : true } )
        setFilteredParticipations( newFilteredParticipations )
    }

    const filterFunc = item => {
        if ( searchText?.trim() === "" )
            return true
        const searchTextRegExp = new RegExp( searchText?.replace( /[.*+?^${}()|[\]\\]/g, '\\$&' ), 'i' )
        return item.student_id?.match( searchTextRegExp ) || item.student_name?.match( searchTextRegExp )
    }

    const removeFilters = () => {
        const newParams = new URLSearchParams( searchParams )
        newParams.set( 'searchText', "" )
        newParams.set( 'selectedAttempt', "0" )
        setSearchParams( newParams, { replace: true } )
    }

    const handleChangePage = ( event, newPage ) => {
        setPage( newPage )
    }

    const handleChangeRowsPerPage = ( event ) => {
        setRowsPerPage( parseInt( event.target.value, 10 ) )
        setPage( 0 )
    }

    const getData = useCallback( async () => {
        try {
            const [{ data: participationData }] = await Promise.all( [api.fetchExamParticipation( id ), ExamStore.fetchExam( id )] )
            const exam = ExamStore.getExam?.exam
            const fetchedParticipations = participationData.participations || []

            let groupStudents = []
            try {
                groupStudents = ( await Promise.all( ( exam?.batches_ids || [] ).map( groupId => {
                    const params = {}
                    if ( String( groupId ).includes( '-' ) )
                        params.section_assignment_id = String( groupId ).split( '-' )[0]
                    else
                        params.batch_assignment_id = groupId
                    return api.quiz.fetchBatchOrSectionAssignedStudentDetails( params )
                } ) ) ).flatMap( response => {
                    const responseData = response?.data?.data
                    if ( Array.isArray( responseData ) ) return responseData
                    return responseData?.students || []
                } )
            } catch ( err ) {
                console.log( err )
            }

            if ( groupStudents.length === 0 ) {
                setLegacyGroup( true )
            }

            const participatedStudentIds = new Map( fetchedParticipations.map( participation => [String( participation.student_id ), participation] ) )
            const uniqueGroupStudents = Array.from( new Map( groupStudents.map( student => [student.auid, student] ) ).values() )
            const unAttempted = []
            const attempted = []
            uniqueGroupStudents
                .forEach( student => {
                    if ( !participatedStudentIds.has( student.auid ) ) {
                        unAttempted.push( {
                            participation_id: `unattempted-${student.auid}`,
                            student_id: student.auid,
                            student_usn: student.usn,
                            student_name: student.student_name || student.name || 'Not Available',
                            no_participation: true,
                            exam_participation_attempt: 0,
                            exam_participation_status: 'No Attempt',
                        } )
                    } else {
                        const details = participatedStudentIds.get( student.auid )
                        details.student_usn = student.usn
                        attempted.push( details )
                    }
                } )

            if ( uniqueGroupStudents.length === 0 && participatedStudentIds.size > 0 ) {
                setParticipations( fetchedParticipations )
            } else {
                setParticipations( [...sortArrayOfObjectsByKey( attempted, 'student_id' ), ...sortArrayOfObjectsByKey( unAttempted, 'student_id' )] )
            }
            setTotalMarks( participationData.totalMarks )
            setExam( participationData.exam )
        } catch ( err ) {
            console.log( err )

            if ( err?.response?.status === 403 || err?.response?.status === 401 )
                setAccessDenied( true )
            else
                toast( err?.response?.data?.message || "Something went wrong" )
        }
        finally {
            setInProgress( false )
        }
    }, [id, ExamStore] )

    useEffect( () => { getData() }, [getData] )


    useEffect( () => {
        if ( selectedAttempt === 'latest' ) {
            const groupByID = participations.reduce( ( dict, item ) => {
                if ( dict[item.student_id] ) {
                    if ( parseInt( dict[item.student_id].exam_participation_attempt ) < item.exam_participation_attempt )
                        dict[item.student_id] = item
                } else
                    dict[item.student_id] = item
                return dict
            }, {} )
            setFilteredParticipations( Object.values( groupByID ) )
        } else if ( selectedAttempt === 'best' ) {
            const groupByID = participations.reduce( ( dict, item ) => {
                if ( dict[item.student_id] ) {
                    if ( parseInt( dict[item.student_id].exam_participation_score ) < item.exam_participation_score )
                        dict[item.student_id] = item
                } else
                    dict[item.student_id] = item
                return dict
            }, {} )
            setFilteredParticipations( Object.values( groupByID ) )
        } else if ( +selectedAttempt !== 0 ) {
            const filteredList = participations.filter( p => String( p.exam_participation_attempt ) === String( selectedAttempt ) )
            setFilteredParticipations( filteredList )
        } else if ( +selectedAttempt === 0 ) {
            setFilteredParticipations( participations || [] )
        }
    }, [selectedAttempt, participations] )


    const columns = [
        {
            name: <Box width="fit-content" display="flex" gap="10px" alignItems="center">
                Student <IconButton onClick={() => { sortByProperty( "student_name" ) }} sx={{ color: "inherit", width: "20px", height: "20px", "&:hover": { background: "#eee", color: "GrayText" } }} size='small'>
                    {sortState.student_name !== true ? <ArrowDownwardIcon sx={{ fontSize: "14px" }} /> : sortState.student_name !== false ? <ArrowUpwardIcon sx={{ fontSize: "14px" }} /> : <UnfoldMore sx={{ fontSize: "14px" }} />}
                </IconButton>
            </Box>, color: "white", minWidth: "180px", align: "left"
        },
        {
            name: <Box width="fit-content" gap="10px" display="flex" alignItems="center">
                AUID
                <IconButton onClick={() => { sortByProperty( "student_id" ) }} sx={{ color: "whitesmoke", width: "20px", height: "20px", "&:hover": { background: "#eee", color: "GrayText", width: "20px", height: "20px" } }} size='small'>
                    {sortState.student_id !== true ? <ArrowDownwardIcon sx={{ fontSize: "14px" }} /> : sortState.student_id !== false ? <ArrowUpwardIcon sx={{ fontSize: "14px" }} /> : <UnfoldMore sx={{ fontSize: "14px" }} />}
                </IconButton>
            </Box>
            , color: "white", minWidth: "100px", align: "left",
        },
        {
            name: <Box width="fit-content" gap="10px" display="flex" alignItems="center">
                USN
                <IconButton onClick={() => { sortByProperty( "student_usn" ) }} sx={{ color: "whitesmoke", width: "20px", height: "20px", "&:hover": { background: "#eee", color: "GrayText", width: "20px", height: "20px" } }} size='small'>
                    {sortState.student_usn !== true ? <ArrowDownwardIcon sx={{ fontSize: "14px" }} /> : sortState.student_usn !== false ? <ArrowUpwardIcon sx={{ fontSize: "14px" }} /> : <UnfoldMore sx={{ fontSize: "14px" }} />}
                </IconButton>
            </Box>
            , color: "white", minWidth: "100px", align: "left",
        },
        { name: "attempt", color: "white", width: "10px", align: "right" },
        { background: "#f18f08", color: "white", name: "Status", minWidth: "150px" },
        {
            background: "#f18f08", minWidth: "120px", color: "white", name: <Box justifyContent="flex-start" display="flex" gap="10px" alignItems="center">
                Score <IconButton onClick={() => { sortByProperty( "exam_participation_score" ) }} sx={{ color: "inherit", width: "20px", height: "20px", "&:hover": { background: "#eee", color: "GrayText" } }} size='small'>
                    {sortState.exam_participation_score !== true ? <ArrowDownwardIcon sx={{ fontSize: "14px" }} /> : sortState.exam_participation_score !== false ? <ArrowUpwardIcon sx={{ fontSize: "14px" }} /> : <UnfoldMore sx={{ fontSize: "14px" }} />}
                </IconButton>
            </Box>, align: "right"
        },
        { name: "Violations", minWidth: "150px" },
        { name: "Remaining Time", minWidth: "180px" },
        { name: "Device", minWidth: "150px" },
        { name: "Submission type", minWidth: "150px" },
        { name: "Remarks", minWidth: "150px" },
        { name: "", align: "right", styles: { right: "0", zIndex: 20, boxShadow: `-5px 0px 5px 0px ${theme === 'dark' ? "#2d2d2dd7" : "#DBDBDBAB"}` } }
    ]

    const highestScore = !participations[0]?.no_participation ? participations.reduce( ( highestScore, participation ) => {
        return ( +participation?.exam_participation_score > +highestScore?.exam_participation_score ) ? participation : highestScore
    }, participations[0] ) : null

    return (
        <Box minHeight="450px" display="flex" flexDirection="column" overflow="auto" flexGrow={1} minWidth="0" sx={{ padding: "20px" }}>

            {accessDenied && <BlockMessage actions={[{ label: "Back to Participations", action: () => navigate( `/faculty/participations` ) }]} type='error' message='You do not have access to view this exam information.' />}

            {!accessDenied &&
                <BreadcrumbList items={[
                    { link: "/faculty/exams", label: "Exams" },
                    { link: `/faculty/exams/${id}`, label: exam ? exam.exam_name : <CircularProgress sx={{ color: "textSecondary" }} size={12} /> },
                    "Participations"
                ]} />}

            {!accessDenied && <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", overflow: { xs: "initial", md: "auto" }, gap: "20px" }}>
                <Box display="flex" gap={layout.cardsGap} alignItems="flex-start" >
                    <IconButton onClick={() => navigate( `/faculty/exams/${id}` )} size='small' sx={{ border: border[1], marginTop: "3px" }}>
                        {Icons.default.ArrowBack}
                    </IconButton>
                    <Box>
                        <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">{exam ? exam.exam_name : <Skeleton component="p" />} </Typography>
                        <Typography variant="subtitle2" fontSize="12px" color='textSecondary' >Participations</Typography>
                    </Box>
                </Box>

                {!inProgress && highestScore && <Box display="grid" gap="10px" width="100%" gridTemplateColumns={{ md: "repeat(3,1fr)", xs: "repeat(2,1fr)" }}>
                    <CountCard label='Participants (First attempt)' icon={Icons.default.Group} value={participations.filter( p => +p.exam_participation_attempt === 1 ).length} />
                    {highestScore && <CountCard label='Highest Score' icon={Icons.default.AnalyticsIcon} value={`${highestScore.student_name} (${highestScore.exam_participation_score})`} />}
                </Box>}

                {exam && participations && participations.length > 0 && <Box flexGrow={1} display="flex" flexDirection="column" overflow={{ xs: "initial", md: "auto" }} >
                    {participations.length > 0 && <Box display="flex" flexDirection={{ lg: "row", md: "row", sm: "row", xs: "column" }} gap="10px"  >
                        <CustomSearchBar width={{ md: "auto", xs: "100%" }} placeholder='Search students by AUID or name' style={{ marginRight: "0", flexGrow: 1 }} value={searchText} onChange={e => setSearchParamValue( 'searchText', e.target.value )} />
                        <Button ref={attemptsMenuRef} onClick={() => setAttemptsMenuOpen( true )} endIcon={Icons.default.KeyboardArrowDownIcon} variant='contained' disableElevation sx={{ background: `linear-gradient(125.39deg, #139CFF -32.54%, #C54097 110.4%)`, }} >
                            <Typography variant='subtitle2' color="white">{selectedAttempt === "0" ? "All attempts" : `Attempt: ${selectedAttempt}`}</Typography>
                        </Button>
                        <Menu anchorEl={attemptsMenuRef.current} open={attemptsMenuOpen} onClose={() => setAttemptsMenuOpen( false )}>
                            <MenuItem selected={selectedAttempt === "0"} onClick={() => handleAttemptChange( "0" )} sx={{ fontSize: "14px" }} value={"0"}>All attempts</MenuItem>
                            <MenuItem selected={selectedAttempt === "best"} onClick={() => handleAttemptChange( "best" )} sx={{ fontSize: "14px" }} value={"best"}>Best attempt</MenuItem>
                            <MenuItem selected={selectedAttempt === "latest"} onClick={() => handleAttemptChange( "latest" )} sx={{ fontSize: "14px" }} value="latest">Latest attempt</MenuItem>
                            {new Array( parseInt( Math.max( 1, +( participations.reduce( ( prev, item ) => item.exam_participation_attempt > prev ? item.exam_participation_attempt : prev, 1 ) || 1 ) ) ) ).fill( 1 ).map( ( e, index ) => (
                                <MenuItem selected={selectedAttempt === `${index + 1}`} onClick={() => handleAttemptChange( index + 1 )} sx={{ fontSize: "14px" }} key={index} value={index + 1}>Attempt: {index + 1}</MenuItem>
                            ) )}
                        </Menu>
                        <Tooltip disableInteractive title="Export participation data as CSV file">
                            <Button disableElevation onClick={() => exportExamParticipation( filteredParticipations.filter( filterFunc ), totalMarks, !legacyGroup )} sx={{ width: { sm: "150px", xs: "100%" } }} variant='contained' color='customThemeColor' endIcon={<Upload />}>
                                Export All
                            </Button>
                        </Tooltip>
                    </Box>}
                    {!inProgress && participations && participations.length > 0 &&
                        <Box border={border[1]} borderRadius="5px" boxShadow={shadows[1]} maxHeight={600} display="flex" flexDirection="column" minHeight="300px" marginTop="10px" overflow="auto">
                            {legacyGroup && <Typography sx={{ m: 0.5, fontSize: "0.875rem", display: "flex", gap: 1, alignItems: "flex-start", p: 1, borderRadius: layout.borderRadius, bgcolor: palette.warning.light + "22", color: palette.warning.main }} >
                                <Typography component="span" sx={{ color: "inherit", fontSize: "1.2rem" }} >{Icons.default.Warning}</Typography>
                                The batch or section assigned to this resource belongs to a previous academic year and is no longer active, so the list of students who did not attempt this exam cannot be displayed.
                            </Typography>}
                            {filteredParticipations.filter( filterFunc ).length > 0 && <TableContainer className='slim-custom-scrollbar' sx={{ flexGrow: 1, overflow: "auto", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                                <Table stickyHeader>
                                    <TableHeader columns={columns} />
                                    <TableBody>
                                        {filteredParticipations.filter( filterFunc )
                                            .slice( page * rowsPerPage, page * rowsPerPage + rowsPerPage )
                                            .map( ( participation, index ) => (
                                                <ParticipationAttemptRow totalMarks={totalMarks} fetchParticipations={getData} exam={exam} participation={participation} key={participation.participation_id} index={index} columns={columns} />
                                            ) )}
                                    </TableBody>
                                </Table>
                            </TableContainer >}
                            {filteredParticipations.filter( filterFunc ).length > 0 && <TablePagination
                                sx={{ background: palette.contentBg, position: "sticky", bottom: 0, right: 0, left: 0, minHeight: "55px" }}
                                rowsPerPageOptions={[50, 100, 150, 200]}
                                component="div"
                                className='no-scrollbar'
                                count={filteredParticipations.filter( filterFunc ).length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />}

                            {filteredParticipations.filter( filterFunc ).length === 0 && <Box height="100%" flexGrow={1} padding="20px" bgcolor={palette.contentBg} borderRadius="5px" display="flex" flexDirection="column" gap="10px" justifyContent={'center'} alignItems="center">
                                <img width="200px" src={noParticipationImg} alt="No participtions" />
                                <Typography padding="20px" variant='subtitle2' >No participants for applied filters!</Typography>
                                <Button startIcon={Icons.default.FilterAltOff} sx={{ textTransform: "capitalize" }} variant='outlined' onClick={removeFilters} color='error' >Remove filter</Button>
                            </Box>}
                        </Box>
                    }
                </Box>}
                {!inProgress && participations.length === 0 && <Box height="100%" flexGrow={1} bgcolor={palette.contentBg} borderRadius="5px" display="flex" flexDirection="column" gap="10px" justifyContent={'center'} alignItems="center">
                    <img width="200px" src={noParticipationImg} alt="No participtions" />
                    <Typography padding="20px" variant='subtitle2' >No participants yet!</Typography>
                </Box>}
                {inProgress && <DataLoadingSpinner waitingMessage="Loading exam participations..." />}
            </Box >}
        </Box >
    )
}
